import classNames from 'classnames';
import classnames from 'classnames';
import getConfig from 'next/config';
import Head from 'next/head';
import Link from 'next/link';
import React, { useEffect, useRef } from 'react';

import { AuthorInfo } from '@sravni/react-ugc/lib/AuthorInfo';
import { useIsMobile } from '@sravni/react-utils';
import { getOptimizedSrc } from '@sravni/ugc/lib/optimizedImgV2';
import { getFullUserName } from '@sravni/ugc/lib/utils/getFullUserName';

import { LIST_BY_TAGS } from '../../../../constants/routes';
import { sendLikeEvent, sendViewCommentEvent } from '../../../../helpers/analytics';
import { generateContentFromHtml } from '../../../../helpers/generateContentFromHtml';
import { checkIsArticle, checkIsNews, checkIsPartnerNews } from '../../../../helpers/material';
import { getMaterialLink } from '../../../../helpers/news';
import { useTotalComments } from '../../../../hooks/comments';
import { useAuthors, useDictionaries } from '../../../../hooks/news';
import { useCopyText } from '../../../../hooks/useCopyText';
import { useUser } from '../../../../hooks/users';
import { isServer } from '../../../../utils/isServer';
import { lineClamp } from '../../../../utils/lineClamp';
import { createLinkProps } from '../../../../utils/routing';
import { CommentsButton } from '../../../CommentsSection/commentsButton';
import { Share } from '../../../Share';
import { ShareClickHandler } from '../../../Share/types';
import { AdvPopUp } from '../AdvPopUp';
import { IArticleProps } from '../Article';
import { LikeArticleButton } from '../LikeArticleButton';

import styles from './article-header.module.scss';
import { ArticleInfo } from './info';

export interface IArticleHeaderProps extends Pick<IArticleProps, 'material' | 'index'> {
  isHighlight?: boolean;
  commentsRef?: React.RefObject<HTMLDivElement>;
  commentsLink?: { as: string; href: string };
  onToggleLike: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onShareClick?: ShareClickHandler;
  likesCount: number | undefined;
  isArticleLiked: boolean | undefined;
  titleTag?: 'h1' | 'h2';
  titleRef?: React.RefObject<HTMLDivElement>;
}

const { avatarURLPrefix } = getConfig().publicRuntimeConfig;

export const ArticleHeader: React.FC<IArticleHeaderProps> = ({
  material,
  titleTag: TitleTag = 'h1',
  index,
  isHighlight = false,
  commentsRef,
  commentsLink,
  onToggleLike,
  isArticleLiked,
  likesCount,
  titleRef,
}) => {
  const isArticle = checkIsArticle(material);
  const isNews = checkIsNews(material);
  const isPartnerNews = checkIsPartnerNews(material);
  const isMobile = useIsMobile();
  const leadRef = useRef<HTMLDivElement>();
  const { tags, productTags } = useDictionaries();
  const commentsCount = useTotalComments(material.id);
  const isSupportLineClamp = !isServer ? CSS.supports('-webkit-line-clamp: 4') : true;
  const leadClasses = classnames(styles.lead, isMobile ? 'article-comment' : 'article-body', {
    [styles['clampJs-3']]: !isSupportLineClamp && !isMobile,
    [styles['clampJs-4']]: !isSupportLineClamp && isMobile,
  });

  const handleLikeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { as } = getMaterialLink(material);

    onToggleLike(e);

    if (!isArticleLiked) sendLikeEvent(as);
  };

  useEffect(() => {
    if (!isSupportLineClamp) {
      lineClamp(leadRef.current, material.lead);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const [getSelectionText, createTimeTextareaCopy] = useCopyText();
  const authors = useAuthors();
  const profile = useUser();

  const isAuthor = authors.some((author) => {
    return String(author.id) === profile?.sub;
  });

  useEffect(() => {
    leadRef.current.addEventListener('copy', () => {
      const { text, status } = getSelectionText();
      if (status && !isAuthor) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            createTimeTextareaCopy(text);
          })
          .catch(() => {
            createTimeTextareaCopy(text);
          });
      }
    });
  }, [leadRef, createTimeTextareaCopy, getSelectionText, isAuthor]);

  let getShareUrl = '';
  if (material.externalLink) {
    getShareUrl = material.externalLink;
  } else {
    const { webPath } = getConfig().publicRuntimeConfig;
    const link = getMaterialLink(material);

    const formattedLocation = isServer ? '' : window.location.search.replace('?wl=true', '');
    getShareUrl = `${webPath}${link.as}${formattedLocation}`;
  }

  const optimizedSrc = getOptimizedSrc(material.picture?.url, isMobile ? 'm' : 'l');

  useEffect(() => {
    const handleLinkPropagation = (e: MouseEvent) => e.stopPropagation();
    const links = Array.from(leadRef.current.getElementsByTagName('a'));

    links.forEach((link) => link.addEventListener('click', handleLinkPropagation));
    return () => links.forEach((link) => link.removeEventListener('click', handleLinkPropagation));
  }, []);

  const imageBlock = () => {
    if (isNews && !isPartnerNews) return null;
    return index === 0 && isServer ? (
      <div className={styles.imageWrapper}>
        {material.advertisingShieldText && <AdvPopUp title={material.advertisingShieldText} />}
        <img
          alt={material.title}
          src={optimizedSrc}
          width={material.picture?.width}
          height={material.picture?.height}
        />
      </div>
    ) : (
      material.picture && (
        <div className={styles.imageWrapper}>
          {material.advertisingShieldText && <AdvPopUp title={material.advertisingShieldText} />}
          <img
            className="lazy-load"
            data-src={material.picture?.url}
            width={material.picture?.width}
            height={material.picture?.height}
            alt={material.title}
          />
        </div>
      )
    );
  };

  const commentsButton = () => {
    if (commentsLink) {
      return (
        <Link href={commentsLink.href} as={`${commentsLink.as}`}>
          <a>
            <CommentsButton
              commentsCount={commentsCount}
              canCreateNewComments={material.canCreateNewComments}
              commentsRef={commentsRef}
              onClick={sendViewCommentEvent}
            />
          </a>
        </Link>
      );
    }

    return (
      <CommentsButton
        commentsCount={commentsCount}
        commentsRef={commentsRef}
        canCreateNewComments={material.canCreateNewComments}
        onClick={sendViewCommentEvent}
      />
    );
  };

  const likeButton = () => {
    if (material.externalLink) return null;

    return <LikeArticleButton onToggleLike={handleLikeClick} isLiked={isArticleLiked} likesCount={likesCount} />;
  };

  const tagsBlock = () => {
    if (isHighlight && imageBlock()) {
      const tagsArr = material.tags.reduce((output, item) => {
        const tag = tags.find((elem) => elem.name === item) || productTags.find((elem) => elem.name === item);
        if (tag) output.push(tag);
        return output;
      }, []);

      return (
        <div className={styles.tagsWrapper}>
          {tagsArr.map((item) => {
            const { as, href } = createLinkProps('list', LIST_BY_TAGS, {
              tag: item.alias,
            });
            return (
              <Link href={href} as={as} key={as}>
                <a className={styles.tag} key={`tag-${item.alias}`}>
                  {item.name}
                </a>
              </Link>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const author = material.authors.find(
    (el) => el.displayName === material.authorName && el.lastName === material.authorLastName,
  );

  const authorName = getFullUserName({
    firstName: material.authorName,
    lastName: material.authorLastName,
    type: 'long',
  });

  return (
    <>
      {/* делаем прелоад картинки */}
      {index === 0 && !!optimizedSrc && isArticle && isServer && (
        <Head>
          <link rel="preload" as="image" href={optimizedSrc} />
        </Head>
      )}

      <div className={classNames('h-mb-24', { [styles.wrapperHighlight]: isHighlight })}>
        <ArticleInfo material={material} isHighlight={isHighlight} />
        {imageBlock()}
        {tagsBlock()}
        <TitleTag
          id={material.alias}
          className={classNames(styles.title, isMobile ? 'article-h2' : 'article-h1')}
          dangerouslySetInnerHTML={{ __html: material.title }}
          data-trigger="visiter"
          data-alias={material.alias}
          ref={titleRef}
        />
        <div
          ref={leadRef}
          className={leadClasses}
          dangerouslySetInnerHTML={{ __html: material.lead && generateContentFromHtml(material.lead).content }}
        />
        {isArticle && author && (
          <div className={styles.interactive}>
            <AuthorInfo id={author.id} name={authorName} label={author.title} avatarUrl={avatarURLPrefix} hasLink />
          </div>
        )}
        {isNews && isHighlight && (
          <div className={styles.headerBottom}>
            <div className={styles.interactive}>
              <div className={styles.interactiveBlock}>
                <Share shareUrl={getShareUrl} title={material.title} />
                {likeButton()}
                {commentsButton()}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
